import { useState } from 'react';

interface Character {
  name: string;
  title: string;
  description: string;
  imagePath: string;
  element: 'fire' | 'water' | 'air' | 'earth';
}

const characters: Character[] = [
  {
    name: "Ignis & Pyra",
    title: "The Flames of Rebirth",
    description: "Ignis, the roaring inferno, and Pyra, the guiding spark, are the twin embodiments of Fire's relentless energy. Born from the heart of volcanoes, their flames signify both destruction and renewal. Ignis wields unyielding rage to scorch his foes, while Pyra's calm control offers a burning determination. Together, they carry the fiery ambition of their kind, tempered by an unspoken rivalry. Strengths: Overwhelming Earth with fiery might. Weaknesses: Vulnerable to Water's quenching tides.",
    imagePath: "/rumble 2/9769.png",
    element: "fire"
  },
  {
    name: "Aqua & Marina",
    title: "The Eternal Tides",
    description: "Aqua, the unyielding wave, and Marina, the serene lagoon, reflect Water's adaptability and depth. Aqua's torrential power can drown even the fiercest fires, while Marina's soothing currents mend and protect. Hailing from the boundless seas, they embody Water's duality: calm and tempestuous. Their unity ensures that their element always finds a way to flow forward. Strengths: Dousing Fire with relentless precision. Weaknesses: Disrupted by Air's unpredictable gusts.",
    imagePath: "/rumble 2/9874.png",
    element: "water"
  },
  {
    name: "Terra & Gaia",
    title: "The Guardians of the Land",
    description: "Terra, the steadfast mountain, and Gaia, the nurturing soil, represent Earth's enduring strength and life-giving power. Terra's strikes echo with the weight of tectonic force, while Gaia's gentle touch revitalizes her allies. Emerging from the ancient forests and cliffs, they are unshakable protectors of the natural world, rooted in their purpose. Strengths: Resisting Air's transient force. Weaknesses: Consumed by Fire's unrelenting blaze.",
    imagePath: "/rumble 2/9845.png",
    element: "earth"
  },
  {
    name: "Zephyr & Aero",
    title: "The Whispering Winds",
    description: "Zephyr, the mischievous breeze, and Aero, the commanding gale, are the embodiments of Air's freedom and unpredictability. Zephyr's agility confounds opponents, while Aero's raw force sweeps away all resistance. Born from the skies and the breath of storms, they embrace chaos as their ally, weaving through battles with ease. Strengths: Scattering Water's steady flow. Weaknesses: Grounded by Earth's stability.",
    imagePath: "/rumble 2/9410.png",
    element: "air"
  }
];

const CharacterStats = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextCharacter = () => {
    setCurrentIndex((prev) => (prev + 1) % characters.length);
  };

  const previousCharacter = () => {
    setCurrentIndex((prev) => (prev - 1 + characters.length) % characters.length);
  };

  const character = characters[currentIndex];

  const getElementColor = (element: string) => {
    switch (element) {
      case 'fire': return 'text-red-500';
      case 'water': return 'text-blue-500';
      case 'air': return 'text-cyan-400';
      case 'earth': return 'text-emerald-500';
      default: return 'text-white';
    }
  };

  const getElementBg = (element: string) => {
    switch (element) {
      case 'fire': return 'from-red-900/50 via-red-800/30 to-red-900/50';
      case 'water': return 'from-blue-900/50 via-blue-800/30 to-blue-900/50';
      case 'air': return 'from-cyan-900/50 via-cyan-800/30 to-cyan-900/50';
      case 'earth': return 'from-emerald-900/50 via-emerald-800/30 to-emerald-900/50';
      default: return 'from-gray-900/50 via-gray-800/30 to-gray-900/50';
    }
  };

  return (
    <section className="py-24 relative overflow-hidden bg-[#1a0f0f]">
      <div className="max-w-7xl mx-auto px-4 relative">
        <div className="relative flex flex-col md:flex-row justify-center items-center gap-8">
          {/* Character Card */}
          <div className={`relative w-full md:w-[600px] aspect-square bg-black rounded-lg overflow-hidden transform hover:scale-105 transition-transform duration-300 bg-gradient-to-b ${getElementBg(character.element)}`}>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80" />
            <img
              src={character.imagePath}
              alt={character.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-6 left-0 w-full text-center">
              <h3 className={`text-3xl font-bold ${getElementColor(character.element)}`}>
                {character.name}
              </h3>
            </div>
          </div>

          {/* Character Info Card */}
          <div className="relative w-full md:w-[500px] bg-black/80 rounded-lg p-8 backdrop-blur-sm">
            <h3 className={`text-3xl font-bold mb-4 ${getElementColor(character.element)}`}>
              {character.title}
            </h3>
            <p className="text-gray-300 text-lg leading-relaxed">
              {character.description}
            </p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center items-center gap-4 mt-12">
          <button
            onClick={previousCharacter}
            className="p-3 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex gap-3">
            {characters.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-red-600' : 'bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextCharacter}
            className="p-3 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default CharacterStats; 